import { useEffect, useCallback, useState, useRef } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useLocalStorage } from '../hooks';
import { ShopifyCartService } from './services';

export const useSamplerCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
} = {}) => {
  const service = useService(ShopifyCartService);
  const { campaignId } = useCampaign();
  const [cartId, setCartId] = useState(null);
  const [
    campaignIdAddedToAttributesSampler,
    setCampaignIdAddedToAttributesSampler,
  ] = useLocalStorage('ADDED_CAMPAIGN_ID_TO_ATTRIBUTES_SAMPLER', false);
  const creatingRef = useRef(null); //* Updated to hold promise instead of boolean
  const cartIdRef = useRef(cartId);

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('samplerCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        setCampaignIdAddedToAttributesSampler(false);

        const response = await service.createCart();

        if (response?.data?.cart_id) {
          updateCartId(response.data.cart_id);
          return response.data;
        } else {
          return response?.data || null;
        }
      } catch (error) {
        creatingRef.current = null;
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId, setCampaignIdAddedToAttributesSampler]);

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    dedupingInterval: 10000,
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('samplerCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          await createCart();
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const attachCustomer = useCallback(
    async (customerData = {}) => {
      if (!cartId) return null;
      if (!customerData.email) return null;

      try {
        const response = await service.attachCustomer(cartId, customerData);
        await refreshCart(response.data, false);
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    [service, cartId, refreshCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* There is a bug where a cart is created twice. This doesn't fix it directly...
    //* but i found that the createCart call being awaited was better for adding the attribute following.
    //TODO: Refactor to avoid double calls altogether. (...Once i find where it is happening of course)
    const initCart = async () => {
      const savedCartId = Cookies.get('samplerCartId');

      if (savedCartId) {
        setCartId(savedCartId);
      } else if (shouldCreateCart) {
        try {
          const cartData = await createCart();

          if (cartData?.cart_id) {
            await updateCartAttributes(
              { order_type: 'onboarding', sample_box_order: 'true' },
              cartData.cart_id,
            );
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart]);

  // Update cart attributes with campaign_id when cart is first created
  useEffect(() => {
    if (!campaignId || !cartId) return;

    if (campaignIdAddedToAttributesSampler) return;

    if (!campaignIdAddedToAttributesSampler) {
      updateCartAttributes({ campaign_id: campaignId });
      setCampaignIdAddedToAttributesSampler(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, cartId, campaignIdAddedToAttributesSampler]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);
  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    createCart,
    addPetData,
    attachCustomer,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    refreshCart,
    couponCode: getCouponCode(),
  };
};
